import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { IReportsRepository } from "../../../domain/repositories/reports-repository";
import { TInsightsOnTrafficResponse } from "../../../domain/entities/insights-on-traffic-response";
import { TCountryTrafficRankingResponse } from "../../../domain/entities/country-traffic-ranking-response";
import { ReportsFilterContext } from "./ReportsFilterProvider";
import { createBlobDownloader } from "../../../utils/files/downloadBlob";
import { TMapProps } from "../TrafficInsights/TrafficInsightsMap";
import { TChartProps } from "../TrafficInsights/TrafficInsightChart";

type TTrafficInsightsProviderProps = {
  requestParamsIndex: number;
  children: React.ReactNode;
  reportsDatasource: IReportsRepository;
  showCards?: boolean;
  mapProps?: TMapProps;
  chartProps?: TChartProps;
};

export type TTrafficInsightsState = {
  requestParamsIndex: number;
  insightsOnTrafficData?: TInsightsOnTrafficResponse;
  countryTrafficRanking?: TCountryTrafficRankingResponse;
  downloadInsightsOnTrafficCSV: () => void;
  downloadingReports?: boolean;
  isLoadingTraffic: boolean;
  isLoadingGeo: boolean;
  mapProps?: TMapProps;
  chartProps?: TChartProps;
};

const initContext: TTrafficInsightsState = {
  requestParamsIndex: 0,
  downloadInsightsOnTrafficCSV: () => null,
  isLoadingTraffic: true,
  isLoadingGeo: true,
};

export const TrafficInsightsContext = createContext(initContext);

export const TrafficInsightsProvider = ({
  requestParamsIndex,
  children,
  reportsDatasource,
  showCards = true,
  mapProps,
  chartProps,
}: TTrafficInsightsProviderProps) => {
  const [isLoadingTraffic, setIsLoadingTraffic] = useState<boolean>(true);
  const [isLoadingGeo, setIsLoadingGeo] = useState<boolean>(true);

  const [insightsOnTrafficData, setInsightsOnTrafficData] =
    useState<TInsightsOnTrafficResponse>();
  const [countryTrafficRanking, setCountryTrafficRanking] =
    useState<TCountryTrafficRankingResponse>();

  const { requestParams } = useContext(ReportsFilterContext);
  const currentRequestParams = requestParams[requestParamsIndex];

  useEffect(() => {
    setIsLoadingTraffic(true);
    reportsDatasource
      .getInsightsOnTraffic({ ...currentRequestParams, showCards })
      .then(setInsightsOnTrafficData)
      .then(() => setIsLoadingTraffic(false));
  }, [currentRequestParams]);

  useEffect(() => {
    setIsLoadingGeo(true);
    reportsDatasource
      .getCountryTrafficRanking(currentRequestParams)
      .then(setCountryTrafficRanking)
      .then(() => setIsLoadingGeo(false));
  }, [currentRequestParams]);

  const [downloadingReports, setDownloadingReports] = useState<boolean>(false);

  const downloadInsightsOnTrafficCSV = useCallback(() => {
    setDownloadingReports(true);
    return reportsDatasource
      .exportInsightsOnTrafficCSV(currentRequestParams)
      .then(createBlobDownloader("traffic-insights.zip"))
      .finally(() => setDownloadingReports(false));
  }, [currentRequestParams]);

  return (
    <TrafficInsightsContext.Provider
      value={{
        requestParamsIndex,
        insightsOnTrafficData,
        countryTrafficRanking,
        downloadInsightsOnTrafficCSV,
        downloadingReports,
        isLoadingTraffic,
        isLoadingGeo,
        mapProps,
        chartProps,
      }}
    >
      {children}
    </TrafficInsightsContext.Provider>
  );
};
